<!--
  `#/home/conventions` — the three things people get wrong, and the worked examples that show
  them.

  These are one panel on purpose. The examples were previously a separate block further down the
  page, which made them decoration: a diagram nobody can attach to a rule is something you look
  at once. The rule states what to do; the Konva example beside it shows what that looks like in
  the tool it will actually be done in.
-->
<script lang="ts">
  import { href } from '../lib/router.svelte';
  import WorkedExamples from './WorkedExamples.svelte';

  /**
   * The three conventions people get wrong, one sentence each, each pointing at its section.
   *
   * `slug` is the heading id `GuidelinesPage` stamps on the rendered Markdown, so the link
   * lands on the section rather than the top of a 844-line document. It is derived from the
   * heading text by the same rule at both ends — see `slugify` there — so a heading reworded
   * in `annotation_guidelines.md` needs the slug here rewording with it.
   */
  const CONVENTIONS = [
    {
      id: 'hyphen',
      title: 'The hyphen is two completely different things',
      body:
        'A run of dashes (----) is ONE chip meaning "illegible stretch of unknown length" — not ' +
        'four letters — while a single hyphen beside a letter (ḏ- ʾl, h-ḍbt) is an editorial ' +
        'segmentation mark with nothing carved on the stone, and produces no chip at all.',
      section: '§6.3',
      slug: '63-the-hyphen-is-two-completely-different-things',
      example: 'lacuna',
    },
    {
      id: 'sticky',
      title: 'The illegible chip is sticky',
      body:
        'The cursor does not advance past a lacuna chip when you draw, because nobody knows how ' +
        'many glyphs the stretch holds: draw as many boxes as there are glyphs, the first takes ' +
        'the chip and the rest are recorded as insertions, and you move on yourself with →.',
      section: '§6.4',
      slug: '64-the-illegible-chip-is-sticky',
      example: 'lacuna',
    },
    {
      id: 'resolved',
      title: 'Resolved means boxed OR marked not present',
      body:
        'Recording "OCIANA reads a b here and the stone does not have one" (N) finishes that ' +
        'chip exactly as boxing it would — so the progress meter is never a reason to invent a ' +
        'box you do not believe in.',
      section: '§9',
      slug: '9-progress-and-finishing-an-image',
      example: 'restored',
    },
  ];
</script>

<div class="col panels">
  <section class="panel col" aria-labelledby="conventions-heading" data-section="conventions">
    <h2 id="conventions-heading">The three conventions people get wrong</h2>
    <ul class="conventions col">
      {#each CONVENTIONS as item (item.id)}
        <li class="col tight" data-convention={item.id}>
          <strong class="small">{item.title}</strong>
          <span class="small">{item.body}</span>
          <span class="small muted">
            <!-- The section reference is a link now. It named a file and a `§` and left the
                 reader to go and find both; the guidelines are served at `#/guidelines`, and
                 the slug jumps to the heading. -->
            <a href={href({ name: 'guidelines', at: item.slug })}>
              Guidelines <span class="mono">{item.section}</span>
            </a>
            · shown below under <span class="mono">{item.example}</span>
          </span>
        </li>
      {/each}
    </ul>
  </section>

  <WorkedExamples />
</div>

<style>
  .panels {
    gap: 12px;
  }

  .conventions {
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 10px;
  }

  .conventions li {
    border-left: 3px solid var(--line-strong);
    padding-left: 10px;
    max-width: 92ch;
  }

  .tight {
    gap: 2px;
  }
</style>
